/* ═══════════════════════════════════════════════════════════════
   MaterialGateBanner — surfaces the Earliest Safe Install Date
   on a job. Renders inline above other job banners.
   ═══════════════════════════════════════════════════════════════ */

import { useMaterialGate } from "@/data/scheduling/useMaterialGate";
import { Calendar, AlertTriangle, CheckCircle2, Clock, PackageX } from "lucide-react";
import { format, parseISO } from "date-fns";

interface Props {
  jobId: string;
  /** Hide banner when state === "no_orders" (e.g., before ordering even starts). */
  hideWhenIdle?: boolean;
}

export function MaterialGateBanner({ jobId, hideWhenIdle = true }: Props) {
  const verdict = useMaterialGate(jobId);

  if (hideWhenIdle && (verdict.state === "no_orders" || verdict.state === "drafting")) {
    return null;
  }

  const meta = (() => {
    switch (verdict.state) {
      case "blocked":
        return { icon: PackageX, tone: "border-destructive/40 bg-destructive/5 text-destructive", label: "Materials Blocked — Cannot Schedule" };
      case "gated":
        return { icon: Calendar, tone: "border-warning/40 bg-warning/5 text-warning", label: "Install Gated by Materials" };
      case "awaiting_eta":
        return { icon: Clock, tone: "border-muted-foreground/30 bg-muted/30 text-muted-foreground", label: "Awaiting Vendor ETAs" };
      case "partial_received":
        return { icon: AlertTriangle, tone: "border-warning/30 bg-warning/5 text-warning", label: "Partial Receipt — Manager Release Available" };
      case "ready":
        return { icon: CheckCircle2, tone: "border-success/40 bg-success/5 text-success", label: "Materials Ready — Install Cleared" };
      default:
        return { icon: Clock, tone: "border-muted/30 bg-muted/20 text-muted-foreground", label: "Materials" };
    }
  })();

  const Icon = meta.icon;
  const formattedEsid = verdict.esidIso ? format(parseISO(verdict.esidIso), "EEE, MMM d") : null;

  return (
    <div className={`rounded-lg border-2 p-3 mb-4 ${meta.tone}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2 flex-1 min-w-0">
          <Icon className="h-4 w-4 mt-0.5 shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-bold">{meta.label}</span>
              {formattedEsid && (
                <span className="text-xs font-semibold px-2 py-0.5 rounded bg-background/60 border border-current/20">
                  Earliest install: {formattedEsid}
                </span>
              )}
            </div>
            <p className="text-[11px] text-foreground/80 mt-1">{verdict.reason}</p>
            {verdict.outstandingLines.length > 0 && verdict.state !== "ready" && (
              <div className="mt-2 flex flex-wrap gap-1">
                {verdict.outstandingLines.slice(0, 4).map((l, i) => (
                  <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-background/50 border border-current/15">
                    {l.criticality === "critical" && "● "}
                    {l.productName}
                    {l.etaIso ? ` · ${format(parseISO(l.etaIso), "MMM d")}` : " · no ETA"}
                  </span>
                ))}
                {verdict.outstandingLines.length > 4 && (
                  <span className="text-[10px] text-muted-foreground">+{verdict.outstandingLines.length - 4} more</span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}